import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FlexBox, FlexColumn, FlexRow } from '@make-software/cspr-design';
import { BodyText, Button, Link } from '@block-explorer/ui';
import { Trans, useTranslation } from 'react-i18next';
import { getCookie, setCookie } from '../../utils/cookies';

enum ConsentType {
  granted = 'granted',
  denied = 'denied',
  unset = 'unset',
}

export const BannerContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    width: '100%',
    height: ['192px', '116px', '96px', '76px'],
    background: 'rgba(19, 25, 51, 0.92)',
    padding: '16px 0',
    position: 'fixed',
    bottom: 0,
    zIndex: 9999,
    justifyContent: ['center'],
  })
);

const ContentContainer = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: ['column', 'row'],
    maxWidth: theme.maxWidth,
    minWidth: theme.minWidth,
    margin: '0 auto',
    padding: ['0px 16px', '0px 32px', '0px 56px', '0px 0px'],
    '> * + *': {
      marginTop: [16, 0],
    },
  })
);

const TextContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    width: ['100%', '55%', '70%', '100%'],
  })
);

const ButtonContainer = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    '> * + *': {
      marginLeft: [8, 8, 16, 16],
    },
  })
);

export const StyledButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    width: ['140px', '124px', '140px', '151px'],
    height: 36,
    padding: '8px 16px',
  })
);

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.styleguideColors.contentLightBlue,
}));

interface CookieConsentBannerProps {}

const CookieConsentBanner = (props: CookieConsentBannerProps) => {
  const { t } = useTranslation();
  const [consent, setConsent] = useState<ConsentType | null>(null);

  const now = new Date();
  const expiryDate = new Date(now.setMonth(now.getMonth() + 6));

  useEffect(() => {
    const storedConsent = getCookie('consent') as ConsentType;
    setConsent(storedConsent);
  }, []);

  const handleConsent = (updatedConsent) => {
    setConsent(updatedConsent);
    window.gtag('consent', 'update', {
      ad_storage: updatedConsent,
      analytics_storage: updatedConsent,
      personalization_storage: updatedConsent,
      functionality_storage: updatedConsent,
      security_storage: updatedConsent,
    });
    setCookie('consent', updatedConsent, { expires: expiryDate });
  };

  if (consent) {
    return null;
  }

  return (
    <BannerContainer>
      <ContentContainer>
        <TextContainer itemsSpacing={4}>
          <BodyText size={3} scale={'sm'} variation={'white'}>
            <Trans t={t}>
              Dear visitor, we use analytics cookies to improve your experience.
              You have the choice to refuse or accept them.
            </Trans>
          </BodyText>
          <BodyText size={3} scale={'sm'} variation={'darkGray'}>
            <Trans t={t}>
              For any more information about cookies we use, we invite you to
              read our
            </Trans>{' '}
            <StyledLink color="hash" href={'https://cspr.live/privacy#cookies'}>
              {t('cookie policy')}.
            </StyledLink>
          </BodyText>
        </TextContainer>
        <ButtonContainer>
          <StyledButton
            width={'140'}
            color="secondaryBlue"
            onClick={() => handleConsent(ConsentType.denied)}
            height={'36'}
          >
            <Trans t={t}>Refuse</Trans>
          </StyledButton>
          <StyledButton
            width={'140'}
            height={'36'}
            color="primaryRed"
            onClick={() => handleConsent(ConsentType.granted)}
          >
            <Trans t={t}>Accept</Trans>
          </StyledButton>
        </ButtonContainer>
      </ContentContainer>
    </BannerContainer>
  );
};

export default CookieConsentBanner;
